import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  showCart: false,
  cart: {
    userId: null,
    whatsappConfirmation: false,
    whatsappReconfirmation: false,
    discount: 0,
    productsCount: 0,
    total: 0,
    totalWithAnyDiscount: 0,
    totalWithEfectiveDiscount: 0,
    products: {}, // each product stored as: { product, quantity, manualDiscount? }
  },
  user: {
    id: null,
    token: "",
    roles: [],
  },
};

// Helper function to validate a discount based on its date range and active flag
export function validateDiscount(validoDesde, validaHasta, activo) {
  return (
    new Date(validoDesde) <= new Date() &&
    new Date(validaHasta) >= new Date() &&
    activo
  );
}

// Helper function that recalculates the totals for the cart.
// For each product, if a manual discount is provided on its line (manualDiscount),
// that discount percentage is used instead of the built‑in discount.
function recalcCartTotals(cart) {
  let total = 0;
  let totalWithAnyDiscount = 0;
  let totalWithEfectiveDiscount = 0;

  Object.keys(cart.products).forEach((productId) => {
    const line = cart.products[productId];
    const product = line.product;
    const quantity = line.quantity;
    total += product.precioVenta * quantity;

    // Determine discount to use: default is built‑in discount (if valid)...
    let discountToApply = 0;
    let discountApplicable = false;
    if (product.descuentos && product.descuentos[0] &&
      validateDiscount(product.descuentos[0].validoDesde, product.descuentos[0].validaHasta, product.descuentos[0].activo)
    ) {
      discountApplicable = true;
      discountToApply = product.descuentos[0].porcentaje;
    }
    // ...but if a manual discount is provided, override it.
    if (line.manualDiscount !== undefined) {
      discountApplicable = true;
      discountToApply = line.manualDiscount / 100;
    }

    // If discount is applicable, check if there's a limit on discounted units.
    if (discountApplicable) {
      // Use the built‑in discount limit only when there's no manual discount override.
      const discountLimit =
        line.manualDiscount === undefined &&
          product.descuentos && product.descuentos[0] && product.descuentos[0].cantidadDisponible !== null
          ? product.descuentos[0].cantidadDisponible
          : quantity;
      const discountedUnits = Math.min(quantity, discountLimit);
      const nonDiscountedUnits = quantity - discountedUnits;

      // If the discount applies only for effective (cash) payments, then:
      if (product.descuentos && product.descuentos[0] && product.descuentos[0].soloEnEfectivo) {
        totalWithEfectiveDiscount +=
          product.precioVenta * (1 - discountToApply) * discountedUnits +
          product.precioVenta * nonDiscountedUnits;
        // For "any discount" total, no discount is applied.
        totalWithAnyDiscount += product.precioVenta * quantity;
      } else {
        totalWithAnyDiscount +=
          product.precioVenta * (1 - discountToApply) * discountedUnits +
          product.precioVenta * nonDiscountedUnits;
        totalWithEfectiveDiscount +=
          product.precioVenta * (1 - discountToApply) * discountedUnits +
          product.precioVenta * nonDiscountedUnits;
      }
    } else {
      totalWithAnyDiscount += product.precioVenta * quantity;
      totalWithEfectiveDiscount += product.precioVenta * quantity;
    }
  });

  const requestedFixed = Math.max(0, Number(cart.discount) || 0);
  const appliedAny = Math.min(requestedFixed, totalWithAnyDiscount);
  const appliedCash = Math.min(requestedFixed, totalWithEfectiveDiscount);

  cart.total = total;
  cart.totalWithAnyDiscount = totalWithAnyDiscount - appliedAny;
  cart.totalWithEfectiveDiscount = totalWithEfectiveDiscount - appliedCash;
}

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    clearCart: (state) => {
      state.cart = initialState.cart;
    },
    showCart: (state) => {
      state.showCart = true;
    },
    hideCart: (state) => {
      state.showCart = false;
    },
    addToCart: (state, action) => {
      const product = action.payload.product;
      const quantity = action.payload.quantity;
      const previousQuantity = state.cart.products[product.id]
        ? state.cart.products[product.id].quantity
        : 0;
      const newQuantity = previousQuantity + quantity;

      // Update products count: remove previous count for this product, add new total.
      state.cart.productsCount = (state.cart.productsCount - previousQuantity) + newQuantity;
      state.cart.total += product.precioVenta * quantity;

      // Handle built‑in discount if any.
      const descuento = product.descuentos ? product.descuentos[0] : null;
      if (descuento) {
        const exceedsDiscountQuantities =
          quantity > descuento.cantidadDisponible && descuento.cantidadDisponible !== null;
        if (validateDiscount(descuento.validoDesde, descuento.validaHasta, descuento.activo)) {
          if (descuento.soloEnEfectivo) {
            if (exceedsDiscountQuantities) {
              const diffWithoutDiscount = quantity - descuento.cantidadDisponible;
              state.cart.totalWithEfectiveDiscount +=
                product.precioVenta * (1 - descuento.porcentaje) * descuento.cantidadDisponible +
                diffWithoutDiscount * product.precioVenta;
              state.cart.totalWithAnyDiscount +=
                product.precioVenta * quantity;
            } else {
              state.cart.totalWithEfectiveDiscount +=
                product.precioVenta * (1 - descuento.porcentaje) * quantity;
              state.cart.totalWithAnyDiscount +=
                product.precioVenta * quantity;
            }
          } else {
            if (exceedsDiscountQuantities) {
              const diffWithoutDiscount = quantity - descuento.cantidadDisponible;
              state.cart.totalWithEfectiveDiscount +=
                product.precioVenta * (1 - descuento.porcentaje) * descuento.cantidadDisponible +
                diffWithoutDiscount * product.precioVenta;
              state.cart.totalWithAnyDiscount +=
                product.precioVenta * (1 - descuento.porcentaje) * descuento.cantidadDisponible +
                diffWithoutDiscount * product.precioVenta;
            } else {
              state.cart.totalWithAnyDiscount +=
                product.precioVenta * (1 - descuento.porcentaje) * quantity;
              state.cart.totalWithEfectiveDiscount +=
                product.precioVenta * (1 - descuento.porcentaje) * quantity;
            }
          }
        } else {
          state.cart.totalWithEfectiveDiscount += product.precioVenta * quantity;
          state.cart.totalWithAnyDiscount += product.precioVenta * quantity;
        }
      } else {
        state.cart.totalWithEfectiveDiscount += product.precioVenta * quantity;
        state.cart.totalWithAnyDiscount += product.precioVenta * quantity;
      }

      if (!state.cart.whatsappConfirmation && action.payload.whatsappConfirmation) {
        state.cart.whatsappConfirmation = true;
      }
      if (state.cart.whatsappConfirmation && !action.payload.whatsappConfirmation) {
        state.cart.whatsappConfirmation = false;
      }

      state.cart.products[product.id] = {
        product: product,
        quantity: quantity + (state.cart.products[product.id] ? state.cart.products[product.id].quantity : 0),
        petName: action.payload.petName,
        dailyConsumption: action.payload.dailyConsumption,
        estimatedOutOfFoodDate: action.payload.estimatedOutOfFoodDate,
      };

      if (!action.payload.dontOpenCart) {
        state.showCart = true;
      }

      // Recalculate totals after adding product.
      recalcCartTotals(state.cart);
    },
    removeFromCart: (state, action) => {
      const productId = action.payload;
      const product = state.cart.products[productId].product;
      const quantity = state.cart.products[productId].quantity;

      state.cart.productsCount -= quantity;
      state.cart.total -= product.precioVenta * quantity;

      const descuento = product.descuentos ? product.descuentos[0] : null;
      if (descuento) {
        if (validateDiscount(descuento.validoDesde, descuento.validaHasta, descuento.activo)) {
          const exceedsDiscountQuantities =
            state.cart.productsCount > descuento.cantidadDisponible &&
            descuento.cantidadDisponible !== null;
          if (exceedsDiscountQuantities) {
            const diffWithoutDiscount = quantity - descuento.cantidadDisponible;
            if (descuento.soloEnEfectivo) {
              state.cart.totalWithEfectiveDiscount -=
                product.precioVenta * (1 - descuento.porcentaje) * descuento.cantidadDisponible +
                diffWithoutDiscount * product.precioVenta;
              state.cart.totalWithAnyDiscount -= product.precioVenta * quantity;
            } else {
              state.cart.totalWithEfectiveDiscount -=
                product.precioVenta * (1 - descuento.porcentaje) * descuento.cantidadDisponible +
                diffWithoutDiscount * product.precioVenta;
              state.cart.totalWithAnyDiscount -=
                product.precioVenta * (1 - descuento.porcentaje) * descuento.cantidadDisponible +
                diffWithoutDiscount * product.precioVenta;
            }
          } else {
            if (descuento.soloEnEfectivo) {
              state.cart.totalWithEfectiveDiscount -= product.precioVenta * (1 - descuento.porcentaje) * quantity;
              state.cart.totalWithAnyDiscount -= product.precioVenta * quantity;
            } else {
              state.cart.totalWithEfectiveDiscount -= product.precioVenta * (1 - descuento.porcentaje) * quantity;
              state.cart.totalWithAnyDiscount -= product.precioVenta * (1 - descuento.porcentaje) * quantity;
            }
          }
        }
      } else {
        state.cart.totalWithEfectiveDiscount -= product.precioVenta * quantity;
        state.cart.totalWithAnyDiscount -= product.precioVenta * quantity;
      }

      delete state.cart.products[productId];

      if (state.cart.productsCount === 0) {
        state.cart = initialState.cart;
      } else {
        recalcCartTotals(state.cart);
      }
    },
    reduceQuantityFromCart: (state, action) => {
      const productId = action.payload;
      const fullProduct = state.cart.products[productId];
      const product = fullProduct.product;
      const descuento = product.descuentos ? product.descuentos[0] : null;

      state.cart.productsCount -= 1;
      fullProduct.quantity -= 1;
      state.cart.total -= product.precioVenta;

      if (descuento) {
        const exceedsDiscountQuantities =
          fullProduct.quantity >= descuento.cantidadDisponible &&
          descuento.cantidadDisponible !== null;
        if (validateDiscount(descuento.validoDesde, descuento.validaHasta, descuento.activo)) {
          if (descuento.soloEnEfectivo) {
            state.cart.totalWithEfectiveDiscount -=
              product.precioVenta * (exceedsDiscountQuantities ? 1 : 1 - descuento.porcentaje);
            state.cart.totalWithAnyDiscount -= product.precioVenta;
          } else {
            state.cart.totalWithEfectiveDiscount -=
              product.precioVenta * (exceedsDiscountQuantities ? 1 : 1 - descuento.porcentaje);
            state.cart.totalWithAnyDiscount -=
              product.precioVenta * (exceedsDiscountQuantities ? 1 : 1 - descuento.porcentaje);
          }
        }
      } else {
        state.cart.totalWithEfectiveDiscount -= product.precioVenta;
        state.cart.totalWithAnyDiscount -= product.precioVenta;
      }

      if (fullProduct.quantity === 0) {
        delete state.cart.products[productId];
      }
      if (state.cart.productsCount === 0) {
        state.cart = initialState.cart;
      } else {
        recalcCartTotals(state.cart);
      }
    },
    increaseQuantityInCart: (state, action) => {
      const productId = action.payload;
      const product = state.cart.products[productId].product;

      state.cart.productsCount += 1;
      state.cart.total += product.precioVenta;

      const descuento = product.descuentos ? product.descuentos[0] : null;
      if (descuento) {
        const exceedsDiscountQuantities =
          state.cart.products[productId].quantity + 1 > descuento.cantidadDisponible &&
          descuento.cantidadDisponible !== null;
        if (validateDiscount(descuento.validoDesde, descuento.validaHasta, descuento.activo)) {
          if (descuento.soloEnEfectivo) {
            state.cart.totalWithEfectiveDiscount +=
              product.precioVenta * (exceedsDiscountQuantities ? 1 : 1 - descuento.porcentaje);
            state.cart.totalWithAnyDiscount += product.precioVenta;
          } else {
            state.cart.totalWithEfectiveDiscount +=
              product.precioVenta * (exceedsDiscountQuantities ? 1 : 1 - descuento.porcentaje);
            state.cart.totalWithAnyDiscount +=
              product.precioVenta * (exceedsDiscountQuantities ? 1 : 1 - descuento.porcentaje);
          }
        }
      } else {
        state.cart.totalWithEfectiveDiscount += product.precioVenta;
        state.cart.totalWithAnyDiscount += product.precioVenta;
      }

      state.cart.products[productId].quantity += 1;
      recalcCartTotals(state.cart);
    },
    logUser: (state, action) => {
      state.user = action.payload;
    },
    logoutUser: (state) => {
      state.user = initialState.user;
    },
    // The new action to override an existing discount with a manual discount value.
    addDiscountToProduct: (state, action) => {
      const { productId, discountValue } = action.payload;
      // Store the manual discount on the product line.
      if (state.cart.products[productId]) {
        state.cart.products[productId].manualDiscount = discountValue;
      }
      // Recalculate the cart totals to reflect the new discount.
      recalcCartTotals(state.cart);
    },
    setCartFixedDiscount: (state, action) => {
      const n = Number(action.payload);
      state.cart.discount = !Number.isFinite(n) || n < 0 ? 0 : n;
      recalcCartTotals(state.cart);
    },
  },
});

export const {
  clearCart,
  showCart,
  hideCart,
  removeFromCart,
  addToCart,
  reduceQuantityFromCart,
  increaseQuantityInCart,
  logUser,
  logoutUser,
  addDiscountToProduct,
  setCartFixedDiscount,
} = appSlice.actions;

export default appSlice.reducer;
